
/**
 * 文件保存工具类
 * 基于 OPFS (Origin Private File System) 实现高效、流式的本地文件存储
 */
export default class FileSaver {
  private writable: FileSystemWritableFileStream | null = null;
  private queue: Promise<void> = Promise.resolve();
  private isFirst: boolean = true;
  private fileName: string | null = null;

  /**
   * 检查当前环境是否支持 OPFS
   */
  public get isSupported(): boolean {
    return !!navigator.storage?.getDirectory;
  }

  /**
   * 开启新文件记录
   * @param prefix 文件名前缀
   */
  public start(prefix: string = 'chat_data'): void {
    if (!this.isSupported) return;
    if (!this.isFirst) return;

    this.run(async () => {
      await this._close(); // 确保前一个文件已关闭

      const root = await navigator.storage.getDirectory();
      this.fileName = `${prefix}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      
      const handle = await root.getFileHandle(this.fileName, { create: true });
      this.writable = await handle.createWritable();
      
      // 写入 JSON 数组起始符
      await this.writable.write('[');
      this.isFirst = true;
      
      console.log(`[FileSaver] 开始记录: ${this.fileName}`);
    });
  }

  /**
   * 追加数据到当前文件
   * @param data 待保存的数据对象
   */
  public write(data: unknown): void {
    this.run(async () => {
      if (!this.writable) return;
      
      // 处理数组逗号，保持 JSON 格式合法
      const prefix = this.isFirst ? '\n' : ',\n';
      await this.writable.write(`${prefix}  ${JSON.stringify(data)}`);
      this.isFirst = false;
    });
  }

  /**
   * 结束记录并关闭文件
   */
  public close(): void {
    this.run(async () => this._close());
  }

  /**
   * 结束记录并打包保存文件
   */
  public save(): void {
    this.run(async () => this._save());
  }

  /**
   * 执行队列任务（私有）
   */
  private run(task: () => Promise<void>): void {
    this.queue = this.queue.then(task).catch(err => 
      console.error('[FileSaver] 写入错误:', err)
    );
  }

  /**
   * 关闭文件流（私有）
   */
  private async _close(): Promise<void> {
    if (!this.writable) return;
    
    try {
      await this.writable.write('\n]'); // 闭合 JSON 数组
      await this.writable.close();
    } finally {
      this.writable = null;
      this.fileName = null;
    }
  }

  /**
   * 打包文件（私有）
   */
  private async _save(): Promise<void> {
    if (!this.writable) return;
    
    try {
      await this.writable.write('\n]'); // 闭合 JSON 数组
      await this.writable.close();

      // 触发下载
      if (this.fileName) {
        const root = await navigator.storage.getDirectory();
        const handle = await root.getFileHandle(this.fileName);
        const file = await handle.getFile();
        const url = URL.createObjectURL(file);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = this.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log(`[FileSaver] 文件已下载: ${this.fileName}`);
      }
    } finally {
      this.writable = null;
      this.fileName = null;
    }
  }

}
